import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [tempUserData, setTempUserData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);

    // Listener para sincronizar logout entre abas
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        // Token foi removido (logout)
        setUser(null);
        window.location.href = '/login';
      } else if (e.key === 'user' && e.newValue) {
        // User foi atualizado
        try {
          setUser(JSON.parse(e.newValue));
        } catch {}
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, usuario, senha_temporaria, conta_suspensa, motivo_suspensao } = response.data;
      
      // Salvar token sempre (precisa pra pagar upgrade mesmo suspensa)
      localStorage.setItem('token', token);

      // Só bloqueia acesso se trial expirou. Inadimplente entra normal (com banner)
      if (conta_suspensa && motivo_suspensao === 'trial_expirado') {
        return { success: false, suspended: true, cliente_id: usuario.cliente_id, motivo: motivo_suspensao };
      }

      // Se tem senha temporária, não salva user ainda
      if (senha_temporaria) {
        localStorage.setItem('temp_token', token);
        setTempUserData(usuario);
        return { success: true, data: { senha_temporaria, token } };
      }
      
      // Login normal
      const userData = { ...usuario, conta_suspensa: conta_suspensa && motivo_suspensao === 'inadimplencia' };
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true, data: { senha_temporaria: false } };
    } catch (error) {
      const data = error.response?.data;
      return { 
        success: false, 
        message: data?.message || 'Erro ao fazer login'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('temp_token');
    setUser(null);
  };

  const finalizarLogin = async () => {
    const tempToken = localStorage.getItem('temp_token');
    if (!tempToken || !tempUserData) return;

    try {
      // Mover token temporário para definitivo
      localStorage.setItem('token', tempToken);
      localStorage.removeItem('temp_token');
      
      // Usar dados salvos temporariamente
      localStorage.setItem('user', JSON.stringify(tempUserData));
      setUser(tempUserData);
      setTempUserData(null);
    } catch (error) {
      console.error('Erro ao finalizar login:', error);
      logout();
    }
  };

  const hasNivelAcesso = (niveisPermitidos) => {
    if (!user?.nivel_acesso) return false;
    // Admin satisfaz qualquer nível exigido.
    if (user.nivel_acesso === 'admin') return true;
    return niveisPermitidos.includes(user.nivel_acesso);
  };

  // Comparação direta, não via hasNivelAcesso (que devolve true pra admin em tudo).
  const isAdmin = () => hasNivelAcesso(['admin']);
  const isGerente = () => hasNivelAcesso(['admin', 'gerente']);
  const isOperador = () => hasNivelAcesso(['admin', 'gerente', 'operador']);
  const isUsuario = () => hasNivelAcesso(['admin', 'gerente', 'operador', 'usuario']);
  const isVisualizador = () => hasNivelAcesso(['admin', 'gerente', 'operador', 'usuario', 'visualizador']);
  
  const getNivelUsuario = () => user?.nivel_acesso || user?.usuario?.nivel_acesso || 'usuario';
  
  const podeGerenciarNivel = (nivelAlvo) => {
    const hierarquia = ['admin', 'gerente', 'operador', 'usuario', 'visualizador'];
    const meuNivel = user?.nivel_acesso || user?.usuario?.nivel_acesso || 'usuario';
    const meuIndex = hierarquia.indexOf(meuNivel);
    const alvoIndex = hierarquia.indexOf(nivelAlvo);
    return meuIndex <= alvoIndex;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    finalizarLogin,
    hasNivelAcesso,
    isAdmin,
    isGerente,
    isOperador,
    isUsuario,
    isVisualizador,
    getNivelUsuario,
    podeGerenciarNivel,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};